import { addDays, addHours, startOfDay } from "date-fns";
import type React from "react";
import { useTranslation } from "react-i18next";
import type { Contract } from "../../../../types/contract";
import { ManageContractsApp } from "../contractManagementContext";
import { disableManageContracts } from "../ManageContracts";
import ManageContractsRadio from "../ManageContractsRadioCheckbox";

type StartProps = {
	filteredPowerContracts: Contract[];
	hasExtraServiceContracts: boolean;
};

export const Start: React.FunctionComponent<StartProps> = (
	props: StartProps,
) => {
	const { t } = useTranslation();
	const dispatch = ManageContractsApp.useDispatch();

	const { filteredLedgers, selectedForm, selectedForm2, selectedForm4 } =
		ManageContractsApp.useState();
	const { filteredPowerContracts, hasExtraServiceContracts } = props;

	const userHasPaperLedgers =
		filteredLedgers.filter(
			(l) =>
				l.paymentType &&
				l.paymentType.paymentTypeNo !== 3 &&
				l.paymentType?.paymentTypeNo !== 20,
		).length > 0
			? true
			: false;

	return (
		<section className="manage-contracts-step start">
			<h2>{t("manageContracts:manageContracts.StartTitle")}*</h2>

			<div className="form-items">
				{/* Moving */}
				{!disableManageContracts && filteredPowerContracts.length > 0 && (
					<ManageContractsRadio
						radioID="start1"
						radioName="start"
						onChangeFunc={(): void => dispatch(["changeForm", 1])}
						checkedComparison={selectedForm === 1}
						multipleLines={true}
						label={
							<>
								<h4>
									{t("manageContracts:manageContracts.StartOption1Title")}
								</h4>
								<p>{t("manageContracts:manageContracts.StartOption1Body")}</p>
							</>
						}
					/>
				)}
				{/* New contract */}
				{!disableManageContracts && (
					<ManageContractsRadio
						radioID="start2"
						radioName="start"
						onChangeFunc={(): void => dispatch(["changeForm", 2])}
						checkedComparison={selectedForm === 2}
						multipleLines={true}
						label={
							<>
								<h4>
									{t("manageContracts:manageContracts.StartOption2Title")}
								</h4>
								<p>{t("manageContracts:manageContracts.StartOption2Body")}</p>
							</>
						}
					/>
				)}

				{!disableManageContracts && selectedForm === 2 && (
					<div className="sub-items">
						{/* New address */}
						<ManageContractsRadio
							radioID="start2_sub1"
							radioName="start2_sub"
							onChangeFunc={(): void => {
								dispatch(["changeForm2", 1]);
								dispatch([
									"changeStart",
									addDays(addHours(startOfDay(new Date()), 12), 5),
								]);
							}}
							checkedComparison={selectedForm2 === 1}
							multipleLines={true}
							label={
								<>
									<h4>
										{t("manageContracts:manageContracts.StartOption2Sub1Title")}
									</h4>
									<p>
										{t("manageContracts:manageContracts.StartOption2Sub1Body")}
									</p>
								</>
							}
						/>
						{/* Current address */}
						<ManageContractsRadio
							radioID="start2_sub2"
							radioName="start2_sub"
							onChangeFunc={(): void => {
								dispatch(["changeForm2", 2]);
								dispatch([
									"changeStart",
									addDays(addHours(startOfDay(new Date()), 12), 18),
								]);
							}}
							checkedComparison={selectedForm2 === 2}
							multipleLines={true}
							label={
								<>
									<h4>
										{t("manageContracts:manageContracts.StartOption2Sub2Title")}
									</h4>
									<p>
										{t("manageContracts:manageContracts.StartOption2Sub2Body")}
									</p>
								</>
							}
						/>
					</div>
				)}

				{/* Cancel contract */}
				{disableManageContracts && filteredPowerContracts.length > 0 && (
					<ManageContractsRadio
						radioID="start3"
						radioName="start"
						onChangeFunc={(): void => {
							dispatch(["changeForm", 3]);
							dispatch(["terminateLedgerUseCurrentAddress", true]);
						}}
						checkedComparison={selectedForm === 3}
						multipleLines={true}
						label={
							<>
								<h4>
									{t("manageContracts:manageContracts.StartOption3Title")}
								</h4>
								<p>{t("manageContracts:manageContracts.StartOption3Body")}</p>
							</>
						}
					/>
				)}
				{/* Modify current contract */}
				{!disableManageContracts && filteredPowerContracts.length > 0 && (
					<ManageContractsRadio
						radioID="start4"
						radioName="start"
						onChangeFunc={(): void => dispatch(["changeForm", 4])}
						checkedComparison={selectedForm === 4}
						multipleLines={true}
						label={
							<>
								<h4>
									{t("manageContracts:manageContracts.StartOption4Title")}
								</h4>
								<p>{t("manageContracts:manageContracts.StartOption4Body")}</p>
							</>
						}
					/>
				)}

				{selectedForm === 4 && (
					<div className="sub-items">
						{/* Change contract type */}
						<ManageContractsRadio
							radioID="start4_sub1"
							radioName="start4_sub"
							onChangeFunc={(): void => dispatch(["changeForm4", 1])}
							checkedComparison={selectedForm4 === 1}
							multipleLines={true}
							label={
								<>
									<h4>
										{t("manageContracts:manageContracts.StartOption4Sub1Title")}
									</h4>
									<p>
										{t("manageContracts:manageContracts.StartOption4Sub1Body")}
									</p>
								</>
							}
						/>
						{/* Change billing if currently paper */}
						{userHasPaperLedgers && (
							<ManageContractsRadio
								radioID="start4_sub2"
								radioName="start4_sub"
								onChangeFunc={(): void => dispatch(["changeForm4", 2])}
								checkedComparison={selectedForm4 === 2}
								multipleLines={true}
								label={
									<>
										<h4>
											{t(
												"manageContracts:manageContracts.StartOption4Sub2Title",
											)}
										</h4>
										<p>
											{t(
												"manageContracts:manageContracts.StartOption4Sub2Body",
											)}
										</p>
									</>
								}
							/>
						)}
						{/* Add extra services */}
						{hasExtraServiceContracts && (
							<ManageContractsRadio
								radioID="start4_sub3"
								radioName="start4_sub"
								onChangeFunc={(): void => {
									dispatch(["changeForm4", 3]);
									dispatch([
										"changeStart",
										addHours(startOfDay(new Date()), 12),
									]);
								}}
								checkedComparison={selectedForm4 === 3}
								multipleLines={true}
								label={
									<>
										<h4>
											{t(
												"manageContracts:manageContracts.StartOption4Sub3Title",
											)}
										</h4>
										<p>
											{t(
												"manageContracts:manageContracts.StartOption4Sub3Body",
											)}
										</p>
									</>
								}
							/>
						)}
					</div>
				)}
			</div>
		</section>
	);
};

export default Start;
